/**
 * THE VISITOR'S SIDE OF THE CONVERSATION MAKES A SOUND TOO — P2-51.
 *
 * ── WHY THIS IS NOT `apps/web/src/notify/tone.ts` ────────────────────────
 *
 * That module is 600 lines: an `AudioContext`, a synthesised fallback pattern, a
 * state machine over the autoplay block, a cross-tab lease, three named sounds.
 * Every one of those exists because an AGENT'S alarm is load-bearing — a missed
 * beep is a missed customer, and a console that is silently muted must be able
 * to say so.
 *
 * None of it transfers. This runs on A CUSTOMER'S PAGE, out of a byte budget its
 * owner did not choose to spend (`tools/ci/size-limit.json`, "widget app
 * javascript"), and the thing it announces is one reply the visitor is about to
 * read anyway. A missed chime costs nothing. So this is an `HTMLAudioElement`
 * and twenty lines, and the difference in weight between the two is the whole
 * argument.
 *
 * ── ONE ELEMENT, REUSED, CREATED LAZILY ──────────────────────────────────
 *
 * Lazily because a widget that never receives a reply should never construct an
 * audio element at all — most visitors ask nothing. Reused because a fresh
 * element per reply leaks one per message for the life of the page, and a chat
 * is the one place that number does not stay small.
 *
 * ── A BLOCKED `play()` IS NOT AN ERROR ───────────────────────────────────
 *
 * Browsers refuse audio until the user has gestured at the page, and `play()`
 * REJECTS when they do. In this widget the gesture has almost always happened —
 * a visitor who is about to receive a reply typed a message to get it — but
 * "almost always" is not always: an agent can open a conversation the visitor
 * abandoned, and a proactive greeting reaches a page nobody has touched.
 *
 * The rejection is swallowed, and unlike the console's block it is not reported
 * anywhere. There is nothing to report it TO: a visitor has no settings screen,
 * no notification chip and no reason to care that a chime was suppressed. The
 * console's whole "a muted notifier must not pretend to work" argument is about
 * somebody whose JOB depends on hearing it.
 */
import chimeUrl from './agent-reply.mp3';

export interface Chime {
  /**
   * Unlock this element from a normal widget interaction without making sound.
   * Must be called synchronously from the interaction that triggered it.
   */
  prime(): void;
  /** Play once. Never throws, never awaits, and does nothing if audio is refused. */
  play(): void;
}

export function createChime(): Chime {
  let element: HTMLAudioElement | null = null;

  return {
    prime(): void {
      if (typeof Audio !== 'function') return;

      element ??= new Audio(chimeUrl);
      // A muted play inside the visitor's first normal gesture unlocks THIS
      // element without turning a tap on the composer into a sound. Pausing only
      // after playback starts avoids a browser treating the attempt as cancelled
      // before it has recorded the gesture permission.
      element.muted = true;
      element.currentTime = 0;
      void element.play().then(
        () => {
          element?.pause();
          if (element !== null) {
            element.currentTime = 0;
            element.muted = false;
          }
        },
        () => {
          if (element !== null) {
            element.muted = false;
          }
        },
      );
    },

    play(): void {
      // `Audio` is absent in a non-browser runtime — this module is imported by
      // a composable that a component test would mount, and jsdom does not implement playback.
      // Checking the constructor rather than try/catching the call keeps the
      // absence a branch instead of an exception.
      if (typeof Audio !== 'function') return;

      element ??= new Audio(chimeUrl);
      // REWOUND, not restarted from wherever it stopped. Two replies arriving
      // within the sound's own length would otherwise leave the second one
      // silent, because `play()` on an element already playing is a no-op.
      element.currentTime = 0;
      void element.play().catch(() => {
        // Autoplay refused, or the file could not be decoded. See the header:
        // there is nobody to tell, and a console error on a customer's page is
        // noise in somebody else's logs.
      });
    },
  };
}
